import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
} from '@mui/material';
import Grid2 from '@mui/material/Grid';
import {
  IconArrowLeft,
  IconUserPlus,
  IconEdit,
  IconTrash,
  IconPhone,
  IconSearch,
  IconCheck,
  IconX,
  IconFileText
} from '@tabler/icons-react';
import { useSipNumber } from '../contexts/SipNumberContext';
import { useUser } from '../contexts/UserContext';
import { useQueue } from '../contexts/QueueContext';
import { useLocation } from '../contexts/LocationContext';

const SipManagement: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSip, setEditingSip] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    number: '',
    extension: '',
    displayName: '',
    host: '',
    port: 5060,
    username: '',
    password: '',
    secret: '',
    proxy: '',
    locationId: '',
    queueId: '',
    status: 'active'
  });

  const sipNumberContext = useSipNumber();
  const userContext = useUser();
  const queueContext = useQueue();
  const locationContext = useLocation();
  const { sipNumbers, loading, error, fetchSipNumbers, createSipNumber, updateSipNumber, deleteSipNumber } = sipNumberContext;
  const { users } = userContext;
  const { queues } = queueContext;
  const { locations } = locationContext;

  // Load SIP numbers on component mount
  useEffect(() => {
    fetchSipNumbers();
    queueContext.fetchQueues();
    locationContext.fetchLocations();
  }, []);

  const getStatusColor = (status: string) => {
    return status === 'active' ? '#4CAF50' : '#F44336';
  };

  const getStatusText = (status: string) => {
    return status === 'active' ? 'Aktif' : 'Pasif';
  };

  const filteredSipNumbers = sipNumbers.filter(sip => {
    const matchesSearch = (sip.number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (sip.extension || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (sip.queueIds && sip.queueIds.length > 0 && queues.find(q => q._id === (sip.queueIds || [])[0])?.name.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || sip.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleAddSip = () => {
    setEditingSip(null);
    setFormData({
      name: '',
      number: '',
      extension: '',
      displayName: '',
      host: '',
      port: 5060,
      username: '',
      password: '',
      secret: '',
      proxy: '',
      locationId: '',
      queueId: '',
      status: 'active'
    });
    setOpenDialog(true);
  };

  const handleEditSip = (sip: any) => {
    setEditingSip(sip);

    // Extract IDs from populated fields
    const locationId = typeof sip.locationId === 'object' ? sip.locationId._id : (sip.locationId || '');
    
    let queueId = '';
    if (sip.queueIds && sip.queueIds.length > 0) {
      const firstQueue = sip.queueIds[0];
      queueId = typeof firstQueue === 'object' ? firstQueue._id : firstQueue;
    }

    setFormData({
      name: sip.name || '',
      number: sip.number,
      extension: sip.extension,
      displayName: sip.displayName || '',
      host: sip.host,
      port: sip.port,
      username: sip.username,
      password: sip.password || '',
      secret: sip.secret || '',
      proxy: sip.proxy || '',
      locationId: locationId,
      queueId: queueId,
      status: sip.status
    });
    setOpenDialog(true);
  };

  const handleSaveSip = async () => {
    try {
      // FormData'yı backend formatına çevir
      const submitData = {
        ...formData,
        queueIds: formData.queueId ? [formData.queueId] : []
      };
      const { queueId, ...submitDataWithoutQueueId } = submitData;
      
      if (editingSip) {
        // Güncelle
        await updateSipNumber(editingSip._id, submitDataWithoutQueueId);
      } else {
        // Yeni ekle
        await createSipNumber(submitDataWithoutQueueId);
      }
      setOpenDialog(false);
      await fetchSipNumbers(); // Verileri yenile
    } catch (error) {
      console.error('SIP numarası kaydedilemedi:', error);
    }
  };

  const handleDeleteSip = async (sipId: string) => {
    if (window.confirm('Bu SIP numarasını silmek istediğinizden emin misiniz?')) {
      try {
        await deleteSipNumber(sipId);
        await fetchSipNumbers(); // Verileri yenile
      } catch (error) {
        console.error('SIP numarası silinemedi:', error);
      }
    }
  };

  const handleToggleStatus = async (sipId: string) => {
    try {
      const sip = sipNumbers.find(s => s._id === sipId);
      if (sip) {
        await updateSipNumber(sipId, { status: sip.status === 'active' ? 'inactive' : 'active' });
        await fetchSipNumbers(); // Verileri yenile
      }
    } catch (error) {
      console.error('SIP durumu güncellenemedi:', error);
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundImage: 'url(./bg.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      p: 3
    }}>
      {/* Geri İkonu */}
      <Box sx={{ mb: 3 }}>
        <Box 
          sx={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            cursor: 'pointer',
            p: 1,
            borderRadius: 2,
            backgroundColor: 'rgba(255,255,255,0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.2)',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.2)',
              transform: 'translateX(-2px)'
            }
          }}
          onClick={() => window.history.back()}
        >
          <IconArrowLeft size={24} color="white" />
        </Box>
      </Box>

      {/* Ana İçerik */}
      <Box sx={{ px: 10 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" fontWeight="700" color="white" sx={{ fontFamily: 'Monaco, monospace', textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
            SIP Numara Yönetimi
          </Typography>
          <Button
            variant="contained"
            startIcon={<IconUserPlus size={20} />}
            onClick={handleAddSip}
            sx={{
              bgcolor: '#4CAF50',
              '&:hover': { bgcolor: '#45A049' },
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontFamily: 'Monaco, monospace',
              fontWeight: 600
            }}
          >
            Yeni SIP Ekle
          </Button>
        </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <CircularProgress size={60} />
        </Box>
      ) : (
        <>
        {/* İstatistikler */}
        <Grid2 container spacing={3} sx={{ mb: 3 }}>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              border: '1px solid #E0E0E0',
              background: 'white'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight="700" sx={{ fontFamily: 'Monaco, monospace', color: '#2C3E50' }}>
                      {sipNumbers.length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontFamily: 'Monaco, monospace' }}>
                      Toplam SIP
                    </Typography>
                  </Box>
                  <IconPhone size={40} color="#4CAF50" />
                </Box>
              </CardContent>
            </Card>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              border: '1px solid #E0E0E0',
              background: 'white'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight="700" sx={{ fontFamily: 'Monaco, monospace', color: '#2C3E50' }}>
                      {sipNumbers.filter(s => s.status === 'active').length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontFamily: 'Monaco, monospace' }}>
                      Aktif SIP
                    </Typography>
                  </Box>
                  <IconCheck size={40} color="#1976D2" />
                </Box>
              </CardContent>
            </Card>
        </Grid2>

          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              border: '1px solid #E0E0E0',
              background: 'white'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight="700" sx={{ fontFamily: 'Monaco, monospace', color: '#2C3E50' }}>
                      {sipNumbers.filter(s => s.status === 'inactive').length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontFamily: 'Monaco, monospace' }}>
                      Pasif SIP
                    </Typography>
                  </Box>
                  <IconX size={40} color="#FF9800" />
                </Box>
              </CardContent>
            </Card>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              border: '1px solid #E0E0E0',
              background: 'white'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight="700" sx={{ fontFamily: 'Monaco, monospace', color: '#2C3E50' }}>
                      {new Set(sipNumbers.map(s => s.department)).size}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontFamily: 'Monaco, monospace' }}>
                      Departman
                    </Typography>
                  </Box>
                  <IconFileText size={40} color="#9C27B0" />
                </Box>
              </CardContent>
            </Card>
          </Grid2>
        </Grid2>

        {/* Filtreler */}
        <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', border: '1px solid #E0E0E0', background: 'white' }}>
          <CardContent sx={{ p: 3 }}>
            <Grid2 container spacing={2} alignItems="center">
              <Grid2 size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  placeholder="SIP numarası ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <IconSearch size={20} color="#666" style={{ marginRight: 8 }} />
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      fontFamily: 'Monaco, monospace'
                    }
                  }}
                />
              </Grid2>
            <Grid2 size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Durum Filtresi</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Durum Filtresi"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">Tüm Durumlar</MenuItem>
                  <MenuItem value="active">Aktif</MenuItem>
                  <MenuItem value="inactive">Pasif</MenuItem>
                </Select>
              </FormControl>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 3 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  label={`Toplam: ${sipNumbers.length}`}
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  label={`Aktif: ${sipNumbers.filter(s => s.status === 'active').length}`}
                  color="success"
                  variant="outlined"
                />
              </Box>
            </Grid2>
          </Grid2>
        </CardContent>
      </Card>

      {/* SIP Tablosu */}
      <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#F5F5F5' }}>
                <TableCell sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>SIP Numarası</TableCell>
                <TableCell sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>Extension</TableCell>
                <TableCell sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>Host:Port</TableCell>
                <TableCell sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>Kuyruk</TableCell>
                <TableCell sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>Durum</TableCell>
                <TableCell sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>Son Kullanım</TableCell>
                <TableCell sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSipNumbers.map((sip) => (
                <TableRow key={sip._id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconPhone size={20} color="#666" />
                      <Typography variant="body1" fontWeight="500" color="#2C3E50" sx={{ fontFamily: 'Monaco, monospace' }}>
                        {sip.number || 'Bilinmeyen'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="#666" sx={{ fontFamily: 'Monaco, monospace' }}>
                      {sip.extension || 'Bilinmeyen'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="#666" sx={{ fontFamily: 'Monaco, monospace' }}>
                      {sip.host || 'Bilinmeyen'}:{sip.port || '5060'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={sip.queueIds && sip.queueIds.length > 0 ? 
                        (queues.find(q => q._id === (sip.queueIds || [])[0])?.name || 'Bilinmeyen') : 
                        'Atanmamış'}
                      size="small"
                      sx={{
                        bgcolor: '#E3F2FD',
                        color: '#1976D2',
                        fontWeight: 500,
                        fontFamily: 'Monaco, monospace'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={getStatusText(sip.status)}
                        sx={{
                          bgcolor: getStatusColor(sip.status),
                          color: 'white',
                          fontWeight: 500,
                          fontFamily: 'Inter, sans-serif'
                        }}
                      />
                      <Switch
                        checked={sip.status === 'active'}
                        onChange={() => handleToggleStatus(sip._id)}
                        size="small"
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="#666" sx={{ fontFamily: 'Inter, sans-serif' }}>
                      {sip.lastUsed ? new Date(sip.lastUsed).toLocaleString('tr-TR') : 'Hiç kullanılmadı'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Düzenle">
                        <IconButton
                          size="small"
                          onClick={() => handleEditSip(sip)}
                          sx={{ color: '#1976D2' }}
                        >
                          <IconEdit size={20} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Sil">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteSip(sip._id)}
                          sx={{ color: '#F44336' }}
                        >
                          <IconTrash size={20} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* SIP Ekleme/Düzenleme Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>
          {editingSip ? 'SIP Düzenle' : 'Yeni SIP Ekle'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              fullWidth
              label="SIP Adı"
              placeholder="Ana Hat, Destek Hattı, vb."
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              sx={{ fontFamily: 'Inter, sans-serif' }}
            />
            <TextField
              fullWidth
              label="SIP Numarası"
              placeholder="+90 212 555 0123"
              value={formData.number}
              onChange={(e) => setFormData({ ...formData, number: e.target.value })}
              sx={{ fontFamily: 'Inter, sans-serif' }}
            />
            <TextField
              fullWidth
              label="Görünen Ad"
              placeholder="Ana Hat, Destek Hattı, vb."
              value={formData.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              sx={{ fontFamily: 'Inter, sans-serif' }}
            />
            <TextField
              fullWidth
              label="Host"
              placeholder="192.168.1.100"
              value={formData.host}
              onChange={(e) => setFormData({ ...formData, host: e.target.value })}
              sx={{ fontFamily: 'Inter, sans-serif' }}
            />
            <FormControl fullWidth>
              <InputLabel>Lokasyon</InputLabel>
              <Select
                value={formData.locationId}
                onChange={(e) => setFormData({ ...formData, locationId: e.target.value })}
                label="Lokasyon"
              >
                <MenuItem value="">Lokasyon Seçin</MenuItem>
                {locations.map(location => (
                  <MenuItem key={location._id} value={location._id}>
                    {location.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Port"
              type="number"
              placeholder="5060"
              value={formData.port}
              onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) })}
              sx={{ fontFamily: 'Inter, sans-serif' }}
            />
            <TextField
              fullWidth
              label="Username"
              placeholder="sip_user"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              sx={{ fontFamily: 'Inter, sans-serif' }}
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              sx={{ fontFamily: 'Inter, sans-serif' }}
            />
            <TextField
              fullWidth
              label="Secret"
              type="password"
              placeholder="SIP secret (genellikle password ile aynı)"
              value={formData.secret}
              onChange={(e) => setFormData({ ...formData, secret: e.target.value })}
              sx={{ fontFamily: 'Inter, sans-serif' }}
              required
            />
            <TextField
              fullWidth
              label="Proxy"
              placeholder="sip:192.168.1.118:5060 veya sip:example.com"
              value={formData.proxy}
              onChange={(e) => setFormData({ ...formData, proxy: e.target.value })}
              sx={{ fontFamily: 'Inter, sans-serif' }}
              required
              helperText="SIP proxy adresi (örn: sip:host:port formatında)"
            />
            <FormControl fullWidth>
              <InputLabel>Kuyruk</InputLabel>
              <Select
                value={formData.queueId}
                onChange={(e) => setFormData({ ...formData, queueId: e.target.value })}
                label="Kuyruk"
              >
                <MenuItem value="">Kuyruk Seçin</MenuItem>
                {queues.map(queue => (
                  <MenuItem key={queue._id} value={queue._id}>
                    {queue.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Durum</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                label="Durum"
              >
                <MenuItem value="active">Aktif</MenuItem>
                <MenuItem value="inactive">Pasif</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)} sx={{ fontFamily: 'Inter, sans-serif' }}>
            İptal
          </Button>
          <Button
            onClick={handleSaveSip}
            variant="contained"
            sx={{
              bgcolor: '#4CAF50',
              '&:hover': { bgcolor: '#45A049' },
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600
            }}
          >
            {editingSip ? 'Güncelle' : 'Ekle'}
          </Button>
        </DialogActions>
      </Dialog>
        </>
      )}
      </Box>
    </Box>
  );
};

export default SipManagement;
